'use client'

import { useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  clickEvent,
  viewScreenEvent,
  getOfflineVerificationFeedback,
  sendOfflineVerificationFeedback,
  transformFeedbackRatingsResponse,
  UpdateFeedbackRatingOptionsArgs,
} from '@marketplace-web/conversation-feedback/conversation-feedback-data'

import FeedbackFormModal from '../FeedbackFormModal'
import { isStringTruthy, removeFeedbackUrlParam } from './utils'

type Props = {
  transactionId: number
}

const OfflineVerificationFeedback = ({ transactionId }: Props) => {
  const { relativeUrl, searchParams, replaceHistoryState, urlQuery } = useBrowserNavigation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { track } = useTracking()

  const { offline_verification_feedback_seller } = searchParams
  const showFeedback = isStringTruthy(offline_verification_feedback_seller)
  const urlWithoutFeedback = removeFeedbackUrlParam(relativeUrl, urlQuery)

  const { transformedData: feedbackRatings, fetch: fetchOfflineVerificationFeedback } = useFetch(
    getOfflineVerificationFeedback,
    transformFeedbackRatingsResponse,
  )

  useEffect(() => {
    if (!showFeedback) return

    replaceHistoryState(urlWithoutFeedback)
  }, [showFeedback, replaceHistoryState, urlWithoutFeedback])

  useEffect(() => {
    if (!feedbackRatings) return

    setIsModalOpen(true)
    track(viewScreenEvent({ screen: 'offline_verification_feedback_form' }))
  }, [setIsModalOpen, feedbackRatings, track])

  useEffect(() => {
    if (!showFeedback) return

    fetchOfflineVerificationFeedback(transactionId)
  }, [showFeedback, transactionId, fetchOfflineVerificationFeedback])

  const handleModalClose = () => {
    setIsModalOpen(false)

    track(
      clickEvent({
        screen: 'offline_verification_feedback_form',
        target: 'close_screen',
      }),
    )
  }

  const handleFeedbackUpdate = async ({
    rating,
    message,
    feedbackStatementIds,
    finalized,
  }: UpdateFeedbackRatingOptionsArgs) => {
    if (!finalized) {
      await sendOfflineVerificationFeedback({ transactionId, rating })

      return
    }

    setIsModalOpen(false)

    await sendOfflineVerificationFeedback({
      transactionId,
      rating,
      comment: message,
      feedbackStatementIds,
      finalized,
    })
  }

  if (!isModalOpen) return null

  return (
    <FeedbackFormModal
      isOpen
      ratings={feedbackRatings}
      onClose={handleModalClose}
      onSubmit={handleFeedbackUpdate}
      isCloseActionVisible={false}
      isInputWithoutRatingOptions
    />
  )
}

export default OfflineVerificationFeedback
